import { create } from 'zustand'
import type { User, SubscriptionInfo } from '@shared/types/api'
import { updateCurrentAssistant } from '@shared/api/userPreferencesApi'
import { updateSelectedOrganization } from '@shared/api/users'
import { assistantsApi } from '@shared/api/assistants'

interface AuthState {
  user: User | null
  currentAssistantId: string | null
  selectedOrganizationId: string | null // null only during initial loading before backend responds
  subscriptionInfo: SubscriptionInfo | null // Subscription limits and usage from backend

  // Actions
  setUser: (user: User | null) => void
  setCurrentAssistant: (assistantId: string | null) => void
  setSelectedOrganizationId: (organizationId: string) => void
  setSubscriptionInfo: (info: SubscriptionInfo | null) => void
  logout: () => void

  // Sync helpers
  syncCurrentAssistant: (assistantId: string | null) => Promise<void>
  syncSelectedOrganization: (organizationId: string) => Promise<void>
  switchToOrganization: (organizationId: string) => Promise<void>
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  currentAssistantId: null,
  selectedOrganizationId: null, // null during initial loading, always a UUID after backend responds
  subscriptionInfo: null,

  setUser: (user) => set({ user }),

  setCurrentAssistant: (assistantId) => set({ currentAssistantId: assistantId }),

  setSelectedOrganizationId: (organizationId) => set({ selectedOrganizationId: organizationId }),

  setSubscriptionInfo: (info) => set({ subscriptionInfo: info }),

  logout: () => set({ user: null, currentAssistantId: null, selectedOrganizationId: null, subscriptionInfo: null }),

  // Sync current assistant to server
  syncCurrentAssistant: async (assistantId: string | null) => {
    const state = get()
    if (!state.user?.id) return

    try {
      await updateCurrentAssistant(state.user.id, assistantId)
      set({ currentAssistantId: assistantId })
    } catch (error) {
      console.error('Failed to sync current assistant:', error)
      throw error
    }
  },

  // Sync selected organization to server
  syncSelectedOrganization: async (organizationId: string) => {
    const state = get()
    if (!state.user?.id) return

    try {
      await updateSelectedOrganization(state.user.id, organizationId)
      set({ selectedOrganizationId: organizationId })
    } catch (error) {
      console.error('Failed to sync selected organization:', error)
      throw error
    }
  },

  // Switch to organization: sync org + auto-select first assistant
  switchToOrganization: async (organizationId: string) => {
    const state = get()
    if (!state.user?.id) return

    // 1. Switch organization
    await updateSelectedOrganization(state.user.id, organizationId)
    set({ selectedOrganizationId: organizationId })

    // 2. Load assistants of the new organization and select the first one
    try {
      const assistants = await assistantsApi.getByOrganizationId(organizationId)
      const firstAssistantId = assistants.length > 0 ? assistants[0].id : null
      await updateCurrentAssistant(state.user.id, firstAssistantId)
      set({ currentAssistantId: firstAssistantId })
    } catch (error) {
      // If loading assistants fails, clear current assistant
      try {
        await updateCurrentAssistant(state.user.id, null)
      } catch { /* ignore */ }
      set({ currentAssistantId: null })
    }
  },
}))
